import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { LogoMark } from "@/components/art/Poster";
import { http } from "@/lib/api";
import HostOverview from "@/components/host/HostOverview";
import HostRsvps from "@/components/host/HostRsvps";
import HostPhotos from "@/components/host/HostPhotos";
import HostEvent from "@/components/host/HostEvent";

function HostLogin({ error }) {
  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/host";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <main className="emerald-cloth flex min-h-screen items-center justify-center px-5">
      <div className="w-full max-w-sm rounded-[24px] border border-[var(--brass)] bg-[var(--ivory)] p-8 text-center" data-testid="host-login">
        <LogoMark className="mx-auto h-16 w-16" />
        <p className="eyebrow mt-4">Host area</p>
        <h1 className="font-display mt-2 text-4xl font-semibold text-[var(--emerald)]">Backstage, please.</h1>
        <p className="mt-2 text-sm">Only the host’s Google account can enter.</p>
        {error && <p role="alert" className="mt-4 rounded-xl bg-[#fbe9e6] p-3 text-sm font-medium text-[#8a2525]" data-testid="host-login-error">{error}</p>}
        <button type="button" onClick={signIn} className="btn-primary mt-6 w-full" data-testid="host-google-login-button">Sign in with Google</button>
        <Link to="/" className="mt-4 inline-flex min-h-[44px] items-center text-sm underline underline-offset-4">Back to the invitation</Link>
      </div>
    </main>
  );
}

function Dashboard({ user, onLogout }) {
  const [eventData, setEventData] = useState(null);
  const [rsvps, setRsvps] = useState({ items: [], stats: {} });
  const [photos, setPhotos] = useState([]);
  const loadEvent = useCallback(() => http.get("/host/event").then((r) => setEventData(r.data)), []);
  const loadRsvps = useCallback(() => http.get("/host/rsvps").then((r) => setRsvps(r.data)), []);
  const loadPhotos = useCallback(() => http.get("/host/photos").then((r) => setPhotos(r.data)), []);
  useEffect(() => {
    Promise.all([loadEvent(), loadRsvps(), loadPhotos()]).catch(() => {});
  }, [loadEvent, loadRsvps, loadPhotos]);

  const tab = "rounded-full px-4 py-2 text-sm font-semibold data-[state=active]:bg-[var(--emerald)] data-[state=active]:text-[var(--ivory)]";
  return (
    <main className="paper min-h-screen pb-20" data-testid="host-dashboard">
      <header className="emerald-cloth text-[var(--ivory)]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-5 sm:px-8">
          <div className="flex items-center gap-3">
            <LogoMark className="h-10 w-10" />
            <div>
              <p className="font-display text-2xl font-semibold leading-none">Host courtyard</p>
              <p className="text-xs text-[#EADFC6]">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="btn-ghost min-h-[40px] border-[rgba(255,247,232,.5)] bg-transparent text-[var(--ivory)] hover:bg-white/10">View invitation</Link>
            <button type="button" onClick={onLogout} className="btn-ghost min-h-[40px] border-[rgba(255,247,232,.5)] bg-transparent text-[var(--ivory)] hover:bg-white/10" data-testid="host-logout-button"><LogOut className="h-4 w-4" aria-hidden="true" /> Log out</button>
          </div>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-5 pt-8 sm:px-8">
        <Tabs defaultValue="overview">
          <TabsList className="h-auto flex-wrap justify-start gap-1 rounded-full bg-[var(--ivory-2)] p-1">
            <TabsTrigger value="overview" className={tab} data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="rsvps" className={tab} data-testid="tab-rsvps">RSVPs</TabsTrigger>
            <TabsTrigger value="photos" className={tab} data-testid="tab-photos">Photos</TabsTrigger>
            <TabsTrigger value="event" className={tab} data-testid="tab-event">Event settings</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-8"><HostOverview eventData={eventData} rsvps={rsvps} photos={photos} /></TabsContent>
          <TabsContent value="rsvps" className="mt-8"><HostRsvps rsvps={rsvps} reload={loadRsvps} /></TabsContent>
          <TabsContent value="photos" className="mt-8"><HostPhotos photos={photos} reload={loadPhotos} eventData={eventData} setEventData={setEventData} /></TabsContent>
          <TabsContent value="event" className="mt-8">{eventData && <HostEvent eventData={eventData} setEventData={setEventData} />}</TabsContent>
        </Tabs>
      </div>
    </main>
  );
}

export default function Host() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);
  const authError = location.state?.authError;

  useEffect(() => {
    if (user || authError) {
      setChecking(false);
      return;
    }
    http.get("/auth/me").then((r) => setUser(r.data)).catch(() => {}).finally(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    await http.post("/auth/logout").catch(() => {});
    setUser(null);
    navigate("/host", { replace: true, state: {} });
  };

  if (checking) return <main className="emerald-cloth min-h-screen" />;
  if (!user) return <HostLogin error={authError} />;
  return <Dashboard user={user} onLogout={logout} />;
}
